/**
 * 后台菜单定义(2026-10-26 按产品重组)。
 * 每个产品一个分组,组内顺序:概览/分析 → 客户资源 → 节点/基础设施 → 产品/设置。
 * 横向分组:客户与财务 / 工单 / 基础设施总览 / 内容 / 系统。
 * 只改入口,不改任何路由路径;同一路径全树只出现一次。
 * 菜单(AdminLayout)和命令面板(CommandPalette)共用这份数据。
 *
 * 节点类型:
 *   { path, zh, en, badge?, tKey? }          叶子(tKey:zh 是英文 i18n key,渲染时 t())
 *   { section: '小标题', items: [...] }       组内小标题(antd type:'group',不增加层级)
 *   { key, zh, icon, items: [...] }           一级分组
 *   { key, zh, icon, path, en }               一级直达叶子
 */
const p = (path, zh, en, badge) => ({ path, zh, en, ...(badge ? { badge } : {}) });

export const ADMIN_MENU = [
    { key: '/dashboard', icon: 'dashboard', path: '/dashboard', zh: '仪表盘', en: 'Dashboard' },
    {
        key: 'g-customer', icon: 'team', zh: '客户与财务',
        items: [
            p('/users', '用户', 'Users'),
            { ...p('/users/abuse', 'Abuse protection', 'Abuse protection'), tKey: true },
            p('/billing/subscriptions', '订阅', 'Subscriptions'),
            p('/billing/invoices', '账单', 'Invoices'),
            p('/billing/coupons', '优惠码', 'Coupons'),
            p('/billing/affiliate', '推广返利', 'Affiliate'),
            p('/billing/quotas', '用量重算', 'Quota recalculation'),
            p('/billing/products', '产品目录', 'Product catalog'),
            p('/billing/currencies', '货币与汇率', 'Currencies exchange rates'),
        ],
    },
    {
        key: 'g-support', icon: 'support', zh: '工单',
        items: [
            p('/support/tickets', '工单', 'Support tickets', 'tickets'),
            p('/support/departments', '工单部门', 'Ticket departments'),
        ],
    },
    {
        key: 'g-cdn', icon: 'global', zh: 'CDN',
        items: [
            { section: '概览', items: [
                p('/cdn/analytics', 'CDN 流量分析', 'CDN traffic analytics'),
                p('/cdn/ops-alerts', 'CDN 运维告警', 'CDN ops alerts'),
            ] },
            { section: '客户资源', items: [
                p('/cdn/domains', 'CDN 域名', 'CDN domains'),
                p('/cdn/workers', 'Workers', 'Workers'),
            ] },
            { section: '安全', items: [
                p('/cdn/waf-rulesets', 'WAF 规则集', 'WAF rulesets'),
                p('/cdn/crs', 'CRS 规则', 'CRS rules'),
                p('/cdn/global-rules', '全局防火墙规则', 'Global firewall rules'),
                p('/cdn/banlist', 'CDN 封禁 IP', 'CDN banned IPs banlist'),
            ] },
            { section: '节点与网络', items: [
                p('/cdn/nodes', 'CDN 节点', 'CDN nodes'),
                p('/cdn/node-groups', 'CDN L2 分组', 'CDN L2 node groups', 'l2'),
                p('/cdn/topology', 'CDN L2 拓扑', 'CDN L2 topology', 'l2'),
                p('/cdn/ip-pool', 'CDN IP 池', 'CDN IP pool'),
                p('/cdn/node-sync-status', 'CDN 节点同步状态', 'CDN node sync status'),
                p('/cdn/anycast', 'CDN Anycast', 'CDN anycast'),
                p('/cdn/origin-routes', 'CDN 回源选路', 'CDN origin routes'),
                p('/cdn/origin-quality', 'CDN 回源质量', 'CDN origin quality'),
                p('/cdn/global-probe', 'CDN 全球测量', 'CDN global probe'),
                p('/cdn/probe-vantages', 'CDN 观测点', 'CDN probe vantages'),
            ] },
            { section: '调度与 DNS', items: [
                p('/cdn/groups', 'CDN 分组 (CNAME)', 'CDN groups CNAME'),
                p('/cdn/lines', 'CDN 线路', 'CDN lines'),
                p('/cdn/line-records', 'CDN 线路记录', 'CDN line records'),
                p('/cdn/dns-sync', 'CDN 线路 DNS 同步', 'CDN line DNS sync'),
            ] },
            { section: '设置', items: [
                p('/system/cdn', 'CDN 全局设置', 'CDN global settings'),
            ] },
        ],
    },
    {
        key: 'g-dns', icon: 'apartment', zh: 'DNS',
        items: [
            p('/dns/analytics', 'DNS 查询分析', 'DNS query analytics'),
            p('/dns/zones', 'DNS 区域', 'DNS zones'),
            p('/dns/nodes', 'DNS 节点', 'DNS nodes'),
            p('/dns/placement', 'DNS 服务器组分配', 'DNS placement server groups'),
            p('/dns/topology', 'DNS 拓扑', 'DNS topology'),
            p('/dns/cidr', 'DNS CIDR 库', 'DNS CIDR library'),
            p('/dns/banlist', 'DNS 黑名单', 'DNS banlist'),
        ],
    },
    {
        key: 'g-evm', icon: 'cloudServer', zh: '云服务器',
        items: [
            { section: '概览', items: [
                p('/evm/monitor', '云服务器节点监控', 'EVM node monitor'),
                p('/evm/quota', '配额与超额', 'EVM quota overage'),
            ] },
            { section: '客户资源', items: [
                p('/evm/vms', '云服务器实例', 'EVM VMs instances'),
                p('/evm/subscriptions', '云服务器订阅与续费', 'EVM subscriptions renewals'),
                p('/evm/pending', '云服务器待开通', 'EVM pending provisions'),
                p('/evm/eips', '弹性 IP', 'Elastic IPs EIP'),
                p('/evm/vpc', 'VPC', 'VPC'),
                p('/evm/vpc-peering', 'VPC 对等与跨区计费', 'VPC peering'),
                p('/evm/security-groups', '安全组', 'Security groups'),
                p('/evm/port-forward', '端口转发', 'Port forwarding'),
                p('/evm/backups', '备份管理', 'EVM backups'),
                p('/evm/bgp', 'BGP 宣告', 'BGP announcements'),
            ] },
            { section: '节点与基础设施', items: [
                p('/evm/nodes', '云服务器节点', 'EVM nodes'),
                p('/evm/zones', '云服务器可用区', 'EVM zones availability'),
                p('/evm/ip-pools', '云服务器 IP 池', 'EVM IP pools'),
                p('/evm/storage-pools', '云服务器存储池', 'EVM storage pools'),
                p('/evm/ceph', 'Ceph 存储集群', 'Ceph clusters'),
                p('/evm/vds', 'VDS', 'VDS dedicated'),
                p('/evm/overcommit', '超售', 'EVM overcommit'),
                p('/evm/topology', '云服务器拓扑', 'EVM topology'),
            ] },
            { section: '产品与设置', items: [
                p('/evm/products', '云服务器产品', 'EVM products plans'),
                p('/evm/images', '系统镜像', 'EVM images ISO'),
                p('/evm/templates', '云服务器模板', 'EVM templates'),
                p('/evm/startup-scripts', '启动脚本', 'Startup scripts'),
            ] },
        ],
    },
    {
        key: 'g-container', icon: 'container', zh: '独享容器',
        items: [
            p('/evm/containers/list', '容器实例', 'Container instances'),
            p('/evm/containers/pending', '容器待开通', 'Container pending'),
            p('/evm/containers/nodes', '容器节点', 'Container nodes'),
            p('/evm/containers/plans', '容器套餐', 'Container plans'),
            p('/evm/containers/templates', '容器模板', 'Container templates'),
            p('/evm/containers/settings', '容器设置', 'Container settings'),
        ],
    },
    { key: '/dedicated', icon: 'hdd', path: '/dedicated', zh: '独立服务器', en: 'Dedicated servers' },
    {
        key: 'g-host-shared', icon: 'shop', zh: '共享主机',
        items: [
            p('/host-shared/statistics', '共享主机用量', 'Shared hosting statistics'),
            p('/host-shared/vhosts', '共享主机站点', 'Shared hosting vhosts sites'),
            p('/host-shared/pending', '共享主机待开通', 'Shared hosting pending'),
            p('/host-shared/nodes', '共享主机节点', 'Shared hosting nodes'),
            p('/host-shared/webapp-apps', '应用商店', 'Web app store'),
        ],
    },
    {
        key: 'g-domain', icon: 'idcard', zh: '域名注册',
        items: [
            p('/domain-registration/domains', '注册域名', 'Registered domains'),
            p('/domain-registration/verification', '注册人验证', 'Registrant verification'),
            p('/domain-registration/contact-templates', '联系人模板', 'Contact templates'),
            p('/domain-registration/pricing', '域名定价与特价', 'Domain pricing promotions'),
            p('/domain-registration/tld-pricing', '域名后缀定价', 'TLD pricing'),
        ],
    },
    {
        key: 'g-ssl', icon: 'safety', zh: 'SSL 证书',
        items: [
            p('/ssl/stats', '证书统计', 'SSL statistics'),
            p('/ssl/orders', '证书订单', 'SSL orders certificates'),
            p('/ssl/products', 'SSL 证书产品', 'SSL products'),
            p('/ssl/codesigning', '代码签名产品', 'Code signing products'),
        ],
    },
    {
        key: 'g-webmail', icon: 'mail', zh: '企业邮箱',
        items: [
            p('/webmail/stats', '邮件统计与队列', 'Mail stats queue'),
            p('/webmail/domains', '邮件域名', 'Mail domains'),
            p('/webmail/users', '邮箱账号', 'Mailboxes accounts'),
            p('/webmail/aliases', '邮箱别名', 'Mail aliases'),
            p('/webmail/rules', '邮件规则', 'Mail rules'),
            p('/webmail/blacklist', '邮件黑白名单', 'Mail blacklist whitelist'),
            p('/webmail/spam-reports', '举报审核', 'Spam reports'),
            p('/webmail/clusters', '邮件集群', 'Mail clusters'),
            p('/webmail/nodes', '邮件节点', 'Mail nodes'),
            p('/webmail/settings', '邮件集群配置', 'Mail settings'),
        ],
    },
    {
        key: 'g-tm', icon: 'send', zh: '事务邮件',
        items: [
            p('/tm/overview', '事务邮件总览', 'Transactional mail overview'),
            p('/tm/messages', '发送记录', 'TM messages'),
            p('/tm/streams', '发信流', 'TM streams'),
            p('/tm/risk', '风险事件', 'TM risk events'),
            p('/tm/marketing', '营销识别', 'TM marketing classifier'),
            p('/tm/domains', '发信域名', 'TM sending domains'),
            p('/tm/api-keys', '发信 API 密钥', 'TM API keys'),
            p('/tm/suppressions', '抑制名单', 'TM suppressions'),
            p('/tm/nodes', '发信节点', 'TM nodes'),
            p('/tm/groups', '发信服务分组', 'TM groups'),
            p('/tm/settings', '事务邮件设置', 'TM settings'),
        ],
    },
    {
        key: 'g-s3', icon: 'cloud', zh: '对象存储',
        items: [
            p('/s3/regions', '存储地域', 'S3 regions'),
            p('/s3/buckets', '存储桶', 'S3 buckets'),
            p('/s3/keys', '访问密钥', 'S3 access keys'),
            p('/s3/usage', '用量', 'S3 usage'),
            p('/s3/settings', '价格与限制', 'S3 pricing'),
        ],
    },
    {
        key: 'g-access', icon: 'safety', zh: 'Access / Verify',
        items: [
            p('/access/teams', '团队', 'Yeap Access Verify teams orgs seats'),
            p('/access/gateways', '网关组', 'Yeap Access gateway groups machines'),
            p('/access/settings', '设置', 'Yeap Access beta settings'),
        ],
    },
    {
        key: 'g-sites', icon: 'global', zh: '建站',
        items: [
            p('/sites/list', '站点', 'Site builder sites'),
            p('/sites/components', '组件库', 'Site builder components'),
            p('/sites/payments', '收款设置', 'Site builder payments Stripe Connect'),
            p('/sites/marketing', '邮件营销', 'Site builder email marketing'),
        ],
    },
    {
        key: 'g-disk', icon: 'cloud', zh: '云盘',
        items: [
            p('/disk/overview', '云盘总览', 'Disk overview'),
            p('/disk/accounts', '云盘账号', 'Disk accounts'),
            p('/disk/orgs', '云盘组织', 'Disk organizations'),
            p('/disk/shares', '分享审核', 'Disk shares'),
            p('/disk/links', '直链管理', 'Disk direct links'),
            p('/disk/settings', '云盘设置', 'Disk settings'),
        ],
    },
    {
        key: 'g-lightapp', icon: 'rocket', zh: '轻应用',
        items: [
            p('/lightapp/services', '轻应用实例', 'Light app services'),
            p('/lightapp/templates', '应用模板', 'Light app templates'),
            p('/lightapp/template-groups', '模板分组', 'Light app template groups'),
            p('/lightapp/products', '轻应用套餐', 'Light app products'),
            p('/lightapp/endpoints', '轻应用节点', 'Light app endpoints nodes'),
        ],
    },
    { key: '/llm/users', icon: 'thunderbolt', path: '/llm/users', zh: 'LLM 用户', en: 'LLM users' },
    {
        key: 'g-infra', icon: 'cluster', zh: '基础设施总览',
        items: [
            p('/node', '统一节点总览', 'Unified nodes'),
            p('/node/components', '组件版本', 'Component versions'),
            { section: '上游服务商', items: [
                p('/domain-registration/registrars', '域名注册商', 'Domain registrars'),
                p('/ssl/providers', '证书供应商', 'SSL providers'),
                p('/lightapp/registries', '镜像仓库', 'Container registries'),
            ] },
        ],
    },
    {
        key: 'g-content', icon: 'file', zh: '内容',
        items: [
            p('/blog', '博客', 'Blog'),
            p('/docs', '文档', 'Docs documentation'),
        ],
    },
    {
        key: 'g-system', icon: 'setting', zh: '系统',
        items: [
            p('/system/settings', '系统设置', 'System settings'),
            p('/system/health', '系统监控', 'System health'),
            p('/system/processes', '后台进程', 'Background processes tasks'),
            p('/system/failures', '失败/卡住', 'Failures stuck'),
            p('/system/rate-limit', '速率限制', 'Rate limit'),
            p('/logs/jobs', '任务日志', 'Job logs'),
            p('/logs/orders', '订单日志', 'Order logs'),
            p('/logs/system', '系统日志', 'System logs'),
            p('/logs/transfers', '资源转移', 'Resource transfers'),
        ],
    },
];

/** 全部叶子(带所属分组名),给命令面板和「最近访问」用 */
export const flattenMenu = (menu = ADMIN_MENU) => {
    const out = [];
    const walk = (list, group) => {
        list.forEach((it) => {
            if (it.items) {
                walk(it.items, it.section ? group : it.zh);
                return;
            }
            if (it.path) out.push({ path: it.path, zh: it.zh, tKey: !!it.tKey, en: it.en || '', group: group || '' });
        });
    };
    walk(menu, '');
    return out;
};
